<template>
	<div class="exhibitor-page pt-5 page">
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-skeleton-loader v-if="this.isLoading" v-bind="attrs" type="card" />

			<div v-else-if="exhibitor">
				<div v-if="partnerLogo" class="exhibitor-page__content">
					<div class="exhibitor-page__logo-wrapper">
						<img class="exhibitor-page__logo" :src="partnerLogo" alt="partner logo" />
					</div>
				</div>
				<div
					v-if="
						partnerInfoSection.partner_name ||
						partnerInfoSection.partner_location_title ||
						partnerInfoSection.partner_location
					"
					class="exhibitor-page__name-location mt-2"
				>
					<p class="mb-0">
						<span class="exhibitor-page__info-name">
							{{ partnerInfoSection.partner_name }}
						</span>
					</p>
					<p
						v-if="
							partnerInfoSection.partner_location_title ||
							partnerInfoSection.partner_location
						"
						class="exhibitor-page__info-location mt-3"
					>
						<Icon type="pin" :size="14" color="var(--location-color)" :pr="8" />
						<span
							@click="handlerLocation"
							class="exhibitor-page__info-location-value u-cursor-pointer"
						>
							{{
								partnerInfoSection.partner_location_title ||
								partnerInfoSection.partner_location
							}}
						</span>
					</p>
				</div>
				<!--				buttons section-->
				<div :class="buttonsClass">
					<div
						v-if="hasContactEmail"
						@click="handlerEmail"
						class="exhibitor-page__buttons__groups"
					>
						<a
							class="exhibitor-page__info-site"
							:href="`mailto:${buttonSection.partner_contact_email}`"
							target="_blank"
						>
							<Icon
								type="email"
								:size="24"
								color="var(--button-icon-tint)"
								class="exhibitor-page__icon"
							/>
							<span class="exhibitor-page__info-site-label">Contact</span>
						</a>
					</div>
					<div
						v-if="hasSiteUrl"
						@click="handlerExhibitorLink"
						class="exhibitor-page__buttons__groups"
					>
						<a
							class="exhibitor-page__info-site"
							:href="buttonSection.partner_site_url"
							target="_blank"
						>
							<Icon
								type="link"
								:size="24"
								color="var(--button-icon-tint)"
								class="exhibitor-page__icon"
							/>
							<span class="exhibitor-page__info-site-label">Website</span>
						</a>
					</div>
					<div
						v-if="mapPartners"
						@click="handlerMapIt"
						class="exhibitor-page__buttons__groups"
					>
						<a
							class="exhibitor-page__info-site"
							rel="noopener noreferrer"
							target="_blank"
						>
							<Icon
								type="pin"
								color="var(--button-icon-tint)"
								:size="24"
								class="exhibitor-page__icon"
							/>
							<span class="exhibitor-page__info-site-label">Map It</span>
						</a>
					</div>
					<!--					book mark button-->
					<div class="exhibitor-page__buttons__groups" @click="bookmarksToggle">
						<a class="exhibitor-page__info-site">
							<Icon
								:type="bookmarkStatus ? 'bookmark' : 'bookmark_empty'"
								:size="24"
								color="var(--button-icon-tint)"
								class="exhibitor-page__icon"
							/>
							<span class="exhibitor-page__info-site-label">{{
								bookmarkStatus ? 'Bookmarked' : 'Bookmark'
							}}</span>
						</a>
					</div>
				</div>
				<!--				note add-->
				<div
					:class="[
						'exhibitor-page__note-add mt-2',
						{ 'padding-all': !isEditing, 'padding-top-sides': isEditing },
					]"
				>
					<h3 class="exhibitor-page__title">Note</h3>
					<div class="exhibitor-page__note-add__text-wrapper" v-if="!isEditing">
						<span class="exhibitor-page__note-add__text" @click="editNote">
							{{ follow_note }}
						</span>
						<div class="align-self-end" v-if="follow_note.trim() !== ''">
							<button
								class="exhibitor-page__clear_btn elevation-0"
								@click="clearNote"
							>
								clear
							</button>
						</div>
					</div>
					<div v-if="isEditing" class="comment-form__input-wrapper">
						<textarea
							ref="follow_note_input"
							v-model.trim="follow_note"
							placeholder="Add note..."
							rows="1"
							class="exhibitor-page__text-area"
							@input="autoResize"
							@keydown.enter.prevent="onEnterSave"
							@blur="onBlur"
						/>
					</div>
				</div>
				<div v-if="contactInfoVisible" class="exhibitor-page__contact mt-2">
					<div class="exhibitor-page__section">
						<h3 class="exhibitor-page__title mb-3">Contact Info</h3>
						<div>
							<p
								v-if="contactSection.partner_contact_name"
								class="exhibitor-page__section-row mb-2"
							>
								<Icon type="profile" :size="24" color="var(--button-icon-tint)" />
								<span class="exhibitor-page__section-row-value">
									{{ contactSection.partner_contact_name }}
								</span>
							</p>
							<p
								v-if="contactSection.partner_contact_title"
								class="exhibitor-page__section-row mb-2"
							>
								<Icon type="briefcase" :size="24" color="var(--button-icon-tint)" />
								<span class="exhibitor-page__section-row-value">
									{{ contactSection.partner_contact_title }}
								</span>
							</p>
							<p
								v-if="contactSection.partner_contact_phone"
								class="exhibitor-page__section-row mb-2 u-cursor-pointer"
								@click="copyPhone(contactSection.partner_contact_phone)"
							>
								<Icon type="phone" :size="24" color="var(--button-icon-tint)" />
								<span
									style="color: var(--link)"
									class="exhibitor-page__section-row-value u-cursor-pointer"
								>
									{{ contactSection.partner_contact_phone }}
								</span>
							</p>
							<a
								v-if="contactSection.partner_contact_email"
								class="exhibitor-page__section-row mb-2 text-decoration-none"
								:href="`mailto:${buttonSection.partner_contact_email}`"
							>
								<Icon type="email" :size="24" color="var(--button-icon-tint)" />
								<span
									class="exhibitor-page__section-row-value"
									style="color: var(--link)"
								>
									{{ contactSection.partner_contact_email }}
								</span>
							</a>
							<p
								v-if="
									contactSection.partner_contact_city ||
									contactSection.partner_contact_state
								"
								class="exhibitor-page__section-row mb-0"
							>
								<Icon type="location" :size="24" color="var(--button-icon-tint)" />
								<span class="exhibitor-page__section-row-value">
									{{ contactSection.partner_contact_city }},{{
										contactSection.partner_contact_state
									}}
								</span>
							</p>
						</div>
					</div>
				</div>
				<!--				 details -->
				<div v-if="descriptionVisible" class="exhibitor-page__descriptions-section mt-2">
					<div class="exhibitor-page__section">
						<h3 class="exhibitor-page__title mb-3">Description</h3>
						<p
							v-html="postDescription(detailsSection.partner_description)"
							ref="description"
							class="exhibitor-page__description"
							:style="{
								overflow: isCollapsed ? 'hidden' : 'visible',
								display: '-webkit-box',
								WebkitLineClamp: isCollapsed ? 5 : 'unset',
								WebkitBoxOrient: 'vertical',
							}"
						></p>
						<div
							v-if="isShowAllButtonVisible"
							class="exhibitor-page__section-btn-wrapper d-flex justify-end"
						>
							<button
								type="button"
								class="exhibitor-page__section-btn elevation-0"
								@click="toggleDescription"
							>
								{{ isCollapsed ? 'Show More' : 'Hide' }}
							</button>
						</div>
					</div>
				</div>
				<!--Rating-->
				<div
					v-if="ratingSection && ratingSection.rating"
					class="exhibitor-page__rating mt-2"
				>
					<Rating
						:rating="ratingSection.rating"
						@handleTriggerExhibitorRating="handleTriggerExhibitorRating"
					/>
				</div>
				<!-- file -->
				<div v-if="filesVisible" class="exhibitor-page__files mt-2">
					<div class="d-flex align-center justify-space-between mb-3">
						<h3 class="exhibitor-page__title">Attachments</h3>
						<button
							type="button"
							class="exhibitor-page__section-btn elevation-0"
							@click="toFilesPage"
						>
							See All
						</button>
					</div>
					<ExhibitorFile
						v-for="file in fileSection.uploads_list.slice(0, 5)"
						:key="file.upload_id"
						:file="file"
					/>
				</div>

				<!-- BootStaff-->
				<div v-if="boothStaffVisible" class="exhibitor-page__files mt-2">
					<div class="d-flex justify-space-between align-center mb-3">
						<h3 class="exhibitor-page__title">Booth Staff</h3>
						<button
							type="button"
							class="exhibitor-page__section-btn elevation-0"
							@click="toBoothStaffPage"
						>
							See All
						</button>
					</div>
					<BoothStaffCard
						v-for="boothStaff in boothStaffSection.booth_staff_list"
						:key="boothStaff.user_id"
						:boothStaff="boothStaff"
						backgroundColor="#F3F3F6"
					/>
				</div>

				<!-- gives -->
				<div v-if="giveawaysVisible" class="exhibitor-page__gives mt-2">
					<div class="d-flex justify-space-between align-center mb-3">
						<h3 class="exhibitor-page__title">Giveaways</h3>
						<button
							type="button"
							class="exhibitor-page__section-btn elevation-0"
							@click="toGiveawaysPage"
						>
							See All
						</button>
					</div>

					<div
						v-for="giveaway in giveawaysSection.giveaways_list"
						:key="giveaway.giveaway_id"
						class="exhibitor-page__giveaway mb-2"
					>
						<a
							class="d-flex align-center text-decoration-none"
							:href="giveaway.giveaway_image"
							target="_blank"
						>
							<img
								class="exhibitor-page__giveaway-image"
								:src="giveaway.giveaway_image"
								alt="giveaway image"
							/>
							<p class="exhibitor-page__giveaway-name ml-3">
								{{ giveaway.giveaway_name }}
							</p>
						</a>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageBlocked from '@/components/PageBlocked'
import ExhibitorFile from '@/components/Event/ExhibitorFile'
import Block from '@/mixins/Block.js'
import { mapActions, mapGetters } from 'vuex'
import BoothStaffCard from '@/components/Event/BoothStaffCard.vue'
import Dwh from '@/mixins/Dwh'
import linkifyThreadText from '@/mixins/linkifyThreadText'
import Rating from '@/components/Event/Rating'
import exhibitor from '@/assets/images/profile/exhibitor-default.png'

export default {
	components: {
		BoothStaffCard,
		PageBlocked,
		ExhibitorFile,
		Rating,
	},
	mixins: [Block, Dwh, linkifyThreadText],
	data() {
		return {
			isEditing: false,
			follow_note: '',
			bookmarkStatus: null,
			mapOpen: false,
			isCollapsed: true,
			isShowAllButtonVisible: false,
			isSavingNote: false,
			skipNextBlur: false,
			attrs: {
				elevation: 0,
				class: 'pt-7',
			},
			pageBlockedName: 'events',
		}
	},
	methods: {
		...mapActions('event', [
			'getExhibitorById',
			'toggleBookmark',
			'saveFollowNote',
			'getBoothStaff',
			'clearExhibitor',
		]),
		...mapActions('app', ['setPageTitle', 'showAlert', 'hideAlert']),
		...mapActions('auth', ['getMapRegisterToken']),
		handleTriggerExhibitorRating(value) {
			this.sendTrigger({
				category: 'Exhibitor',
				action: 'click_rating_exhibitor',
				model: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
				data: value,
			})
		},
		onEnterSave() {
			this.skipNextBlur = true
			this.saveNote()
		},
		autoResize() {
			const el = this.$refs.follow_note_input
			if (!el) return
			el.style.height = 'auto'
			el.style.height = el.scrollHeight + 'px'
		},
		copyPhone(value) {
			if (!this.isShowAlert) {
				const phone = value.replace(/\D/g, '')

				this.$copyText(phone).then(() => {
					const closeTimeout = setTimeout(() => {
						this.hideAlert('')
					}, 3000)

					this.showAlert({ message: 'Phone number copied successfully.', closeTimeout })
				})
			}
		},
		getBlockEvent(name, fallback = undefined) {
			const block = (this.event.eventView || []).find((b) => b.block_name === name)
			return block ? block.block_data : fallback
		},

		getBlockData(name, fallback = undefined) {
			const partnerView = (this.exhibitor && this.exhibitor.partnerView) || []
			const block = partnerView.find((b) => b.block_name === name)
			return block ? block.block_data : fallback
		},
		isMapUrlValid(url) {
			try {
				const arr = JSON.parse(this.appCore.parsing_url)
				const link = new URL(url)
				return arr.some((allowed) => link.origin === allowed)
			} catch (e) {
				console.error(e)
				return false
			}
		},
		async mapRegistrationUrl() {
			if (this.buttonSection.partner_map_url) {
				if (this.isMapUrlValid(this.buttonSection.partner_map_url)) {
					try {
						await this.getMapRegisterToken()
						if (!this.mapRegisterToken) {
							console.error('Token not received')
							return
						}
						window.open(
							`${this.buttonSection.partner_map_url}&code=${this.mapRegisterToken}`,
							'_self'
						)
					} catch (e) {
						console.error(e)
					}
				}
			} else if (this.exhibitor.partner_map) {
				window.open(this.exhibitor.partner_map, '_self')
			}
		},
		async handlerLocation() {
			const url = this.partnerInfoSection.partner_location
			if (!url || !url.startsWith('https://')) {
				return
			}

			if (this.isMapUrlValid(url)) {
				try {
					await this.getMapRegisterToken()
					if (!this.mapRegisterToken) {
						console.error('Token not received')
						return
					}
					const urlWithToken = `${url}${url.includes('?') ? '&' : '?'}code=${
						this.mapRegisterToken
					}`
					window.open(urlWithToken, '_self')
				} catch (e) {
					console.error(e)
				}
			} else {
				window.open(url, '_self')
			}
		},
		handlerMapIt() {
			this.mapRegistrationUrl()
			this.sendTrigger({
				category: 'Exhibitors',
				action: 'click_mapit_button',
				module: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
			})
		},
		handlerBookMark() {
			this.sendTrigger({
				category: 'Exhibitors',
				action: 'click_exhibitor_bookmark',
				module: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
			})
		},
		handlerRemoveBookMark() {
			this.sendTrigger({
				category: 'Exhibitors',
				action: 'click_exhibitor_remove_bookmark',
				module: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
			})
		},
		handlerExhibitorLink() {
			this.sendTrigger({
				category: 'Exhibitors',
				action: 'click_exhibitor_link',
				module: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
			})
		},
		handlerEmail() {
			this.sendTrigger({
				category: 'Exhibitors',
				action: 'click_exhibitor_email',
				module: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
			})
		},

		clearNote() {
			this.follow_note = ''
			this.saveFollowNote({
				user_id: this.currentUser.user_id,
				bookmark_entity_type: this.noteSection.bookmark.bookmark_entity_type,
				bookmark_note: null,
				bookmark_entity_id: this.noteSection.bookmark.bookmark_entity_id,
			})
			this.isEditing = true
		},

		async saveNote() {
			if (this.isSavingNote) return

			const text = this.follow_note.trim()
			if (text === '') {
				this.isEditing = true
				return
			}

			try {
				this.isSavingNote = true
				this.isEditing = false
				await this.saveFollowNote({
					user_id: this.currentUser.user_id,
					bookmark_entity_type: this.noteSection.bookmark.bookmark_entity_type,
					bookmark_note: text,
					bookmark_entity_id: this.noteSection.bookmark.bookmark_entity_id,
				})
			} catch (e) {
				console.error(e)
			} finally {
				this.isSavingNote = false
			}
		},
		onBlur() {
			if (this.skipNextBlur) {
				this.skipNextBlur = false
				return
			}
			if (this.follow_note.trim() === '') {
				this.clearNote()
			} else {
				this.saveNote()
			}
		},
		editNote() {
			this.isEditing = true
			this.$nextTick(() => {
				this.$refs.follow_note_input?.focus()
				this.autoResize()
			})
		},
		bookmarksToggle() {
			const newStatus = this.bookmarkStatus ? 0 : 1
			if (newStatus === 1) {
				this.handlerBookMark()
			} else {
				this.handlerRemoveBookMark()
			}
			this.bookmarkStatus = newStatus

			this.toggleBookmark({
				user_id: this.currentUser.user_id,
				bookmark_entity_type: this.buttonSection.bookmark.bookmark_entity_type,
				bookmark_status: newStatus,
				bookmark_entity_id: this.buttonSection.bookmark.bookmark_entity_id,
			}).catch((e) => {
				console.error(e)
				this.bookmarkStatus = newStatus === 1 ? 0 : 1
			})
		},
		checkDescriptionLines() {
			this.$nextTick(() => {
				const description = this.$refs.description
				if (description) {
					const lineHeight = parseFloat(getComputedStyle(description).lineHeight)
					const descriptionHeight = description.scrollHeight
					const maxLinesHeight = lineHeight * 5

					this.isShowAllButtonVisible = descriptionHeight > maxLinesHeight
				}
			})
		},
		toggleDescription() {
			this.isCollapsed = !this.isCollapsed
		},
		toGiveawaysPage() {
			this.$router.push({
				name: 'giveaway-list',
				query: {
					id: this.partnerInfoSection.partner_id,
				},
			})
		},
		toBoothStaffPage() {
			this.sendTrigger({
				category: 'Booth Staff',
				action: 'click_booth_staff_list',
				module: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
			})
			this.$router.push({
				name: 'exhibitor-staff',
				params: {
					id: +this.$route.params.id,
				},
				query: {
					event: this.$route.query.event,
					name: this.partnerInfoSection.partner_name,
				},
			})
		},

		toFilesPage() {
			this.$router.push({
				name: 'exhibitor-files-by-id',
				query: {
					partner_id: this.partnerInfoSection.partner_id,
				},
			})
		},
		exhibitorTriggerView() {
			this.sendTrigger({
				category: 'Exhibitors',
				action: 'view_exhibitor_profile',
				module: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
			})
		},
	},
	watch: {
		exhibitor: {
			immediate: true,
			deep: true,
			handler(newVal) {
				if (newVal) {
					this.exhibitorTriggerView()
					this.follow_note = this.noteSection.bookmark.bookmark_note || ''
					this.isEditing = this.follow_note.trim() === ''
					this.bookmarkStatus = this.buttonSection.bookmark.is_bookmarked
					this.setPageTitle(this.exhibitor.partner_name)
					if (newVal) {
						setTimeout(() => {
							this.checkDescriptionLines()
						}, 500)
					}
				}
			},
		},
	},
	mounted() {
		this.checkDescriptionLines()
		window.addEventListener('resize', this.checkDescriptionLines)
	},
	beforeDestroy() {
		this.setPageTitle('')
		this.clearExhibitor()
		window.removeEventListener('resize', this.checkDescriptionLines)
	},
	computed: {
		...mapGetters('settings', ['appCore']),
		...mapGetters('event', ['isLoading', 'exhibitor', 'exhibitorFollow', 'exhibitorNote']),
		...mapGetters('auth', ['profile', 'mapRegisterToken']),
		...mapGetters('app', ['isShowAlert']),
		partnerLogo() {
			return this.partnerInfoSection.partner_logo || exhibitor
		},
		boothStaffSection() {
			return this.getBlockData('boothStaff', { booth_staff_list: [] })
		},
		giveawaysSection() {
			return this.getBlockData('giveaways', { giveaways_list: [] })
		},
		fileSection() {
			return this.getBlockData('uploads', { uploads_list: [] })
		},
		ratingSection() {
			return this.getBlockData('rating', { rating: null })
		},
		detailsSection() {
			return this.getBlockData('details', { partner_description: '' })
		},
		contactSection() {
			return this.getBlockData('contactsBlock', {
				partner_contact_name: '',
				partner_contact_title: '',
				partner_contact_phone: '',
				partner_contact_email: '',
				partner_contact_city: '',
				partner_contact_state: '',
			})
		},
		buttonSection() {
			return this.getBlockData('buttons', {
				partner_contact_email: '',
				partner_site_url: '',
				partner_map_url: '',
				bookmark: null,
			})
		},
		partnerInfoSection() {
			return this.getBlockData('partnerInfo', {
				partner_logo: '',
				partner_name: '',
				partner_location_title: '',
				partner_location: '',
			})
		},
		noteSection() {
			return this.getBlockData('note', {})
		},
		currentUser() {
			return this.profile
		},
		groupId() {
			return parseInt(this.$route.query.group, 10) || 0
		},
		noteAddStyle() {
			return {
				padding: this.isEditing ? '16px 16px 0 16px' : '16px',
			}
		},
		buttonsClass() {
			return {
				'exhibitor-page__buttons': true,
				'mt-2': true,
				'four-buttons': this.buttonCount === 4,
			}
		},

		buttonCount() {
			let count = 1
			if (this.hasContactEmail) count++
			if (this.hasSiteUrl) count++
			if (this.hasMap) count++
			return count
		},
		filesVisible() {
			const list = this.fileSection.uploads_list || []
			return Array.isArray(list) && list.length > 0
		},
		boothStaffVisible() {
			const list = this.boothStaffSection.booth_staff_list || []
			return Array.isArray(list) && list.length > 0
		},
		giveawaysVisible() {
			const list = this.giveawaysSection.giveaways_list || []
			return Array.isArray(list) && list.length > 0
		},
		descriptionVisible() {
			return !!(
				this.detailsSection.partner_description &&
				String(this.detailsSection.partner_description).trim()
			)
		},
		contactInfoVisible() {
			const c = this.contactSection || {}
			return !!(
				c.partner_contact_name ||
				c.partner_contact_title ||
				c.partner_contact_phone ||
				c.partner_contact_email ||
				c.partner_contact_city ||
				c.partner_contact_state
			)
		},
		hasContactEmail() {
			return !!(this.buttonSection && this.buttonSection.partner_contact_email)
		},
		hasSiteUrl() {
			return !!(this.buttonSection && this.buttonSection.partner_site_url)
		},
		mapPartners() {
			return !!(this.buttonSection && this.buttonSection.partner_map_url)
		},
	},
	created() {
		this.getExhibitorById({ id: +this.$route.params.id, groupId: this.groupId })
	},
}
</script>

<style lang="scss">
.exhibitor-page__note-add__textarea .v-label {
	font-family: 'Inter', sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;
	color: #aaaaaa;
}

.exhibitor-page__section-btn {
	font-size: 14px;
	color: var(--text_button_tint) !important;
	background: transparent;
	text-transform: uppercase;
	cursor: pointer;
	text-decoration: unset;
}

.exhibitor-page {
	&__content {
		margin-top: 0;
	}
	&__text-area {
		width: 100%;
		border: none;
		resize: none;
		overflow: hidden;
		font-family: 'Inter', sans-serif;
		font-size: 14px;
		line-height: 140%;
		font-weight: 400;
		margin-top: 12px;
		&::placeholder {
			font-weight: 400;
			font-size: 16px;
			line-height: 140%;
			letter-spacing: 0;
			color: #9b9b9b;
		}

		&:focus {
			border: none;
			outline: none;
			box-shadow: none;
		}
	}
	&__modal {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 24px;
		align-items: center;
		gap: 16px;
		&__title {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 24px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			color: #4a4a4a;
		}
		&__btn {
			max-width: 115px;
			width: 100%;
			height: 46px;
			background-color: var(--primary_color);
			border-radius: 8px !important;
			color: var(--button-main-color);
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
		}
	}

	&__clear_btn {
		background-color: transparent !important;
		color: var(--text_button_tint) !important;
		text-decoration: unset;
		font-size: 14px;
		font-weight: 700;
		line-height: 17.07px;
		min-width: 0;
		height: 0;
		text-transform: none;
	}

	&__logo {
		width: 100%;
		max-height: 338px;
		object-fit: contain;

		&-wrapper {
			background-color: #fff;
			border-radius: 8px;
			padding: 12px;
			display: flex;
			align-items: center;
			justify-content: center;
			border: 1px solid #e2e2e2;
		}
	}

	&__note-add {
		background-color: #fff;
		border-radius: 8px;
		border: 1px solid #e2e2e2;

		&.padding-all {
			padding: 12px;
		}

		&__text-wrapper {
			display: flex;
			justify-content: space-between;
		}

		&.padding-top-sides {
			padding: 12px;
		}

		&__text {
			margin-top: 12px;
			align-self: end;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 14px;
			line-height: 140%;
			word-wrap: break-word;
			word-break: break-word;
			color: #4a4a4a;
		}
	}

	.exhibitor-page {
		&__buttons {
			display: flex;
			flex-wrap: nowrap;
			justify-content: space-between;
			gap: 8px;

			&__groups {
				flex: 1 2 auto;
				width: 100%;
				height: 92px;
				background-color: #fff;
				border-radius: 12px;
				text-align: center;
				border: 1px solid #e2e2e2;

				a {
					text-decoration: none;
					display: flex;
					flex-direction: column;
					align-items: center;
					gap: 5px;
				}
			}

			&.four-buttons {
				@media (max-width: 479px) {
					flex-wrap: wrap;

					& > .exhibitor-page__buttons__groups {
						flex: 1 1 calc(50% - 4px);
						max-width: calc(50% - 4px);
					}
				}
			}
		}
	}

	&__icon {
		margin-top: 10px;
		margin-bottom: 10px;
	}

	&__info-site-label {
		font-family: 'Inter', sans-serif;
		font-weight: 500;
		font-size: 16px;
		line-height: 100%;
		letter-spacing: 0;
		color: var(--username-category-title);
	}

	&__gives {
		background-color: #fff;
		padding: 12px;
		border-radius: 8px;
		border: 1px solid #e2e2e2;
	}

	&__map {
		background-color: #fff;
		padding: 12px;
		border-radius: 10px;
	}

	&__files {
		background-color: #fff;
		padding: 12px;
		border-radius: 8px;
		border: 1px solid #e2e2e2;
	}

	&__name-location {
		background-color: #fff;
		padding: 12px;
		border-radius: 10px;
		border: 1px solid #e2e2e2;
	}

	&__contact {
		background-color: #fff;
		padding: 12px;
		border-radius: 8px;
		border: 1px solid #e2e2e2;
	}

	&__descriptions-section {
		background-color: #fff;
		padding: 12px;
		border-radius: 8px;
		border: 1px solid #e2e2e2;
	}

	&__info {
		background-color: #fff;
		border-radius: 8px;
		font-family: 'Inter', sans-serif;
		padding: 20px 30px;

		&-site {
			color: var(--accent-color) !important;
			display: flex;
			align-items: center;
			flex-wrap: nowrap;
			text-decoration: none;
			margin-top: 12px;

			&-value {
				font-size: 13px;
				font-weight: 500;
				line-height: 19.37px;
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 1;
				-webkit-box-orient: vertical;
				word-break: break-word;
			}
		}

		&-name {
			font-family: 'Inter', sans-serif;
			color: var(--username-category-title);
			font-weight: 700;
			font-size: 20px;
			line-height: 22px;
			letter-spacing: 0;

			&-label {
				color: var(--accent-color);
				font-size: 14px;
				font-weight: 700;
				line-height: 17.07px;
			}
		}

		&-location {
			display: flex;
			align-items: center;
			flex-wrap: nowrap;
			margin-bottom: 0 !important;

			&-value {
				color: var(--location-color);
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 1;
				-webkit-box-orient: vertical;
				word-break: break-word;
				font-family: 'Inter', sans-serif;
				font-weight: 500;
				font-size: 13px;
				line-height: 100%;
				letter-spacing: 0;
			}

			&-icon {
				color: #9b9b9b !important;
			}
		}
	}

	&__title {
		color: #4a4a4a;
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
	}

	&__section {
		&-row {
			display: flex;
			justify-content: flex-start;
			align-items: center;
			flex-wrap: nowrap;
			margin-bottom: 12px !important;
			background-color: #f3f3f6;
			padding: 12px;
			gap: 8px;
			border-radius: 8px;

			@media (max-width: 480px) {
				flex-direction: column;
			}

			span {
				font-size: 14px;
				line-height: 17.07px;
			}

			&-title {
				color: var(--accent-color);
				margin-right: 8px;
				font-family: 'Inter', sans-serif;
				font-weight: 500;
				font-size: 14px;
				line-height: 100%;
				letter-spacing: 0;
			}

			&-value {
				color: #697583;
				font-family: 'Inter', sans-serif;
				font-weight: 400;
				font-size: 14px;
				line-height: 100%;
				letter-spacing: 0;
			}
		}

		&-btn {
			background-color: transparent !important;
			color: var(--text_button_tint) !important;
			text-transform: capitalize;
			font-weight: 700;
			font-family: 'Inter', sans-serif;
			font-size: 18px !important;
			line-height: 100% !important;
			letter-spacing: 0;
			text-align: center;
			padding: 0;

			&-wrapper {
				display: flex;
				justify-content: center;
			}
		}
	}

	&__giveaway {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		background-color: #f3f3f6;
		padding: 12px;
		gap: 12px;
		border-radius: 8px;
		border: 1px solid #e2e2e2;

		&-image {
			width: 52px;
			height: 52px;
			border-radius: 8px;
			object-fit: cover;
			margin-right: 4px;
		}

		&-name {
			font-family: Inter;
			font-weight: 400;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--username-category-title);
			margin-bottom: 0 !important;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			word-break: break-word;
		}
	}

	&__description {
		font-size: 14px;
		line-height: 17.07px;
		color: #4a4a4a;
		margin-bottom: 0 !important;
	}

	&__divider {
		border-color: #e2e2e2 !important;
		margin: 24px 0;
	}
}

.v-text-field {
	padding-top: 0;
}

.v-textarea textarea {
	font-family: 'Inter', sans-serif;
	font-weight: 400;
	font-size: 14px;
	padding-top: 12px;
	line-height: 1rem;
}

.v-text-field__details {
	display: none;
	min-height: 14px;
}
.v-sheet.v-card {
	border-radius: 8px;
}
</style>
